import { useEffect, useState } from "react";
import Headline from "../Components/Elements/Headline";
import PageContentContainer from "../Components/ContentContainers/PageContentContainer";
import GenericContentCard from "../Components/Cards/CardGenericContent";
import { useLanguage } from "../context/LanguageContext";
import Popup from "../Components/Elements/Popup";
import { useNotifications } from "../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import Toast from "../Components/Elements/Toast";
import NavSidebar from "../Components/Sidebar/NavSidebar";

const Notifications = () => {
  const { copy } = useLanguage();
  const navigate = useNavigate();
  const {
    notifications,
    markNotificationAsRead,
    loadingNotifications,
    notificationError: notificationsErrorContext,
  } = useNotifications();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [sidebarToggled, setSidebarToggled] = useState(false);

  useEffect(() => {
    if (notificationsErrorContext) {
      setToast({ message: notificationsErrorContext, type: "error" });
    }
  }, [notificationsErrorContext]);

  const handleMarkAsRead = async (notificationId) => {
    await markNotificationAsRead(notificationId);
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setIsPopupOpen(true);
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedNotification(null);
  };

  const handleGoToMilestones = () => {
    handleClosePopup();
    navigate("/calendar");
  };

  return (
    <PageContentContainer>
      <Headline
        headline={copy.notification.headline}
        description={copy.notification.description}
      />

      <GenericContentCard noPadding fullScreen>
        <ul className="max-h-full overflow-y-auto">
          {loadingNotifications && notifications.length === 0 && (
            <li className="py-2 px-4">{copy.notification.loading}</li>
          )}
          {!loadingNotifications &&
            notifications.length === 0 &&
            !notificationsErrorContext && (
              <li className="py-6 px-4">{copy.notification.empty}</li>
            )}
          {notificationsErrorContext && !loadingNotifications && (
            <li className="py-6 px-4 text-red-600 text-sm">
              {notificationsErrorContext}
            </li>
          )}

          {notifications.map((n) => (
            <li
              key={n.id}
              className={`py-6 px-4 ${n.read ? "" : "font-bold bg-accent-light"} cursor-pointer card-base-border-bottom hover:bg-[rgba(252,187,137,0.3)]`}
              onClick={() => handleNotificationClick(n)}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm">
                    {n.message.replace(
                      "{today}",
                      new Date(n.scheduledFor).toLocaleDateString()
                    )}
                  </p>
                </div>
                <span className="text-xs ml-2">
                  {new Date(n.createdAt).toLocaleDateString()}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </GenericContentCard>

      {selectedNotification?.type === "WELCOME" && (
        <Popup
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          title={copy.notification.popups.welcome.title}
          buttonPrimaryCTA={copy.notification.popups.welcome.button_primary}
          onClickPrimaryLink={() => {
            setSidebarToggled(true);
            handleClosePopup();
          }}
          buttonSecondaryCTA={copy.notification.popups.welcome.button_secondary}
          onClickSecondaryLink={handleClosePopup}
        >
          {selectedNotification && selectedNotification.metadata && (
            <div className="flex flex-col text-sm">
              {(() => {
                return (
                  <>
                    <p
                      className="!mb-5"
                      dangerouslySetInnerHTML={{
                        __html: copy.notification.popups.welcome.message,
                      }}
                    ></p>
                  </>
                );
              })()}
            </div>
          )}
        </Popup>
      )}

      {selectedNotification?.type === "DAILY_MILESTONE" && (
        <Popup
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          title={copy.notification.popups.daily_milestone.title}
          buttonPrimaryCTA={
            copy.notification.popups.daily_milestone.button_primary
          }
          onClickPrimaryLink={handleGoToMilestones}
          buttonSecondaryCTA={
            copy.notification.popups.daily_milestone.button_secondary
          }
          onClickSecondaryLink={handleClosePopup}
        >
          {selectedNotification && selectedNotification.metadata && (
            <div className="flex flex-col text-sm">
              {(() => {
                const notificationDate = selectedNotification.metadata.date
                  ? new Date(selectedNotification.metadata.date)
                  : new Date(selectedNotification.createdAt);

                const formattedDate = notificationDate.toLocaleDateString(
                  "en-GB",
                  {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  }
                );

                const milestonesCount =
                  selectedNotification.metadata.milestones?.length || 0;
                const goalsCount =
                  selectedNotification.metadata.goalsCount || 0;

                const tasks =
                  milestonesCount === 1
                    ? copy.notification.popups.daily_milestone.tasks_one
                    : copy.notification.popups.daily_milestone.tasks_other;

                const goals =
                  goalsCount === 1
                    ? copy.notification.popups.daily_milestone.goals_one
                    : copy.notification.popups.daily_milestone.goals_other;

                return (
                  <>
                    <p className="!mb-5">
                      {copy.notification.popups.daily_milestone.message
                        .replace("{count}", milestonesCount)
                        .replace("{tasks}", tasks)
                        .replace("{date}", formattedDate)
                        .replace("{goalsCount}", goalsCount)
                        .replace("{goals}", goals)}
                    </p>
                    <p className="font-bold mt-4">
                      {copy.notification.popups.daily_milestone.focus_title}
                    </p>
                    <ul className="list-disc">
                      {selectedNotification.metadata.milestones?.map((m) => (
                        <li key={m.id} className="!ml-6">
                          {m.title}
                          {/* {m.goalTitle && (
                            <span className="text-gray-500 ml-2">
                              ({m.goalTitle})
                            </span>
                          )}  */}
                        </li>
                      ))}
                    </ul>
                    <p className="!mt-5">
                      {copy.notification.popups.daily_milestone.motivation}
                    </p>
                  </>
                );
              })()}
            </div>
          )}
        </Popup>
      )}

      {selectedNotification?.type === "GOAL_COMPLETED" && (
        <Popup
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          title={copy.notification.popups.goal_completed.title}
          buttonPrimaryCTA={
            copy.notification.popups.goal_completed.button_primary
          }
          onClickPrimaryLink={() => {
            handleClosePopup();
            navigate(`/goals/${selectedNotification.metadata.goalId}`);
          }}
          buttonSecondaryCTA={
            copy.notification.popups.goal_completed.button_secondary
          }
          onClickSecondaryLink={handleClosePopup}
        >
          {selectedNotification && selectedNotification.metadata && (
            <div className="flex flex-col text-sm">
              {/*   <p className="!mb-5">
                {copy.notification.popups.goal_completed.message}
              </p>*/}
              <p className="font-bold mt-4">
                {copy.notification.popups.goal_completed.details_title}
              </p>
              <ul className="list-disc">
                <li className="!ml-6 mb-2">
                  <span className="font-semibold">
                    {selectedNotification.metadata.goalTitle}
                  </span>
                </li>
                <li className="!ml-6 mb-2">
                  {copy.notification.popups.goal_completed.milestones_completed.replace(
                    "{count}",
                    selectedNotification.metadata.totalMilestones
                  )}
                </li>
                <li className="!ml-6 mb-2">
                  {copy.notification.popups.goal_completed.completed_on.replace(
                    "{date}",
                    new Date(
                      selectedNotification.metadata.completedAt
                    ).toLocaleDateString()
                  )}
                </li>
              </ul>
              <p className="!mt-5">
                {copy.notification.popups.goal_completed.celebration}
              </p>
            </div>
          )}
        </Popup>
      )}

      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}

      {sidebarToggled && (
        <NavSidebar setSidebarToggled={setSidebarToggled} />
      )}
    </PageContentContainer>
  );
};

export default Notifications;
